/**
 * OrientationBackend — rotation-only shell (egocentric, non-persistent).
 *
 * Delegates orientation to DevicePose (the corrected quaternion pipeline). Each
 * fragment gets a stable bearing on the shell; no parallax, constant depth.
 */

import type { Anchor, CameraPose, SpatialBackend, Look, Vec3 } from './SpatialBackend.js';
import { DevicePose } from './DevicePose.js';

const DEG = Math.PI / 180;

function hashSeed(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function bearingFor(id: string): { yaw: number; pitch: number } {
  const s = hashSeed(id);
  return { yaw: (s % 3600) / 10, pitch: (((s >>> 12) % 700) / 10) - 35 };
}

export class OrientationBackend implements SpatialBackend {
  readonly frame = 'egocentric' as const;
  readonly persistent = false;

  private pose = new DevicePose();
  private anchors = new Map<string, Anchor>();

  async start(): Promise<void> {
    await this.pose.start();
  }
  stop(): void {
    this.pose.stop();
  }
  look(): Look {
    return this.pose.look();
  }

  anchor(id: string): Anchor {
    let a = this.anchors.get(id);
    if (!a) {
      // Egocentric: anchors live on a unit shell around a camera pinned at the
      // origin. Storing a POSITION rather than a bearing means the ordinary
      // projection handles them, and the parallax-free result falls out of the
      // camera never moving — which is exactly what a 3DoF backend can justify.
      const b = bearingFor(id);
      const el = b.pitch * DEG;
      const az = b.yaw * DEG;
      const ce = Math.cos(el);
      const world: Vec3 = { x: ce * Math.sin(az), y: Math.sin(el), z: ce * Math.cos(az) };
      a = { id, world, confidence: 1 };
      this.anchors.set(id, a);
    }
    return a;
  }

  resolve(id: string): Anchor | null {
    return this.anchors.get(id) ?? null;
  }

  confidence(): number {
    return 1;
  }

  /** Rotation only — the camera never leaves the origin. */
  cameraPose(): CameraPose {
    return { position: { x: 0, y: 0, z: 0 }, orientation: this.pose.quaternion() };
  }
}
